import { useCallback, useMemo, useRef, useState } from "react";
import type { AnchorState } from "../../lib/anchoring";
import type { SurfacePointMap } from "../../lib/reviewOrder";
import type { ScrollIntent } from "../../lib/scrollMotion";
import type { ReadCanvasHandle } from "./ReadCanvas";

/**
 * `useReadCanvasSurface` — the read surface's host-side state, on the CANVAS.
 *
 * It replaces `useDoczCanvas`, which painted the server's `rendered_html` into a
 * `.ela-doc` div with `innerHTML` and re-anchored comments by walking DOM text
 * nodes. That engine is gone: the canvas is the only document surface (owner
 * directive 2026-07-28), so there is no second painter to keep in step and no
 * hidden twin to re-anchor against.
 *
 * WHAT IT IS, AND WHAT IT DELIBERATELY IS NOT. `useDoczCanvas` OWNED the paint
 * and the anchoring; this hook owns neither. `<ReadCanvas>` resolves the anchors
 * (against docz blocks, via `resolveReadCanvasAnchors`) and paints the bands, and
 * it is the only thing that can — the geometry lives in wasm behind an imperative
 * handle. So this hook is exactly two things:
 *
 *   · the two REACTIVE values the review rail renders from (`anchorStates`,
 *     `anchorPoints`), held as state because the rail re-renders on them;
 *   · a stable IMPERATIVE facade over the surface handle (`focusAnchor`,
 *     `setPendingType`, `clearPending`, `setLinkedHover`), so hosts do not each
 *     write `ref.current?.method(...)` with their own null handling.
 *
 * It keeps `useDoczCanvas`'s RESULT SHAPE on purpose. Both hosts (the two-pane
 * `ArticleWorkbench` and the single-pane `DocumentViewer`) already destructured
 * that shape, and preserving it is what made the excision a swap rather than a
 * rewrite of two large screens. The one member that could NOT survive is
 * `canvasRef` — it was an attach point for a DOM element that no longer exists —
 * and `onMouseUp`, which read `window.getSelection()`; a canvas selection is not
 * a DOM selection, so selection now arrives through `<ReadCanvas onSelectText>`.
 *
 * EVERY MEMBER IS STABLE. The callbacks close over refs, never over the state
 * they publish, so the result identity moves only when `anchorStates` or
 * `anchorPoints` actually change — i.e. when a re-anchor pass changed what the
 * rail reports. That mattered on the DOM hook (hosts build `useCallback`s naming
 * this result) and it matters here for the same reason.
 */

export interface ReadCanvasSurface {
  /** Attach to `<DoczCanvas readCanvasRef=…>` (and thus `<ReadCanvas ref=…>`). */
  readonly ref: React.RefObject<ReadCanvasHandle | null>;
  /** Per-comment anchor state from the last re-anchor pass. */
  readonly anchorStates: Record<number, AnchorState>;
  /**
   * WHERE each resolved note sits on this surface. `null` until a pass has run —
   * which is NOT the same as "nothing is anchored here", and `lib/reviewOrder`
   * relies on the difference: a note no surface covered falls back to its
   * recorded anchor, a note a surface covered and orphaned does not.
   */
  readonly anchorPoints: SurfacePointMap | null;
  /** Pass to `<DoczCanvas onReadCanvasAnchorStates>`. */
  readonly onAnchorStates: (states: Record<number, AnchorState>) => void;
  /** Pass to `<DoczCanvas onReadCanvasAnchorPoints>`. */
  readonly onAnchorPoints: (points: SurfacePointMap) => void;
  /** Scroll to + bloom a comment's band. `null` when the quote no longer places
   *  — the caller must SAY so (a toast), never pretend the jump happened. */
  readonly focusAnchor: (
    commentId: number,
    intent?: ScrollIntent,
  ) => AnchorState | null;
  /** Recolour the pending selection band as the composer's type pick changes. */
  readonly setPendingType: (type: string) => void;
  /** Clear the pending selection band (cancel / submit). */
  readonly clearPending: () => void;
  /** Card→span glow, or `null` to clear. A no-op on a surface that does not hold
   *  that comment, which is what makes fanning it across both panes correct. */
  readonly setLinkedHover: (commentId: number | null) => void;
  /** Open the composer over the LIVE selection (the toolbar's "New comment").
   *  `false` when nothing is selected. */
  readonly requestComment: () => boolean;
  /** Viewport rect of a whole block by its stored `block_ref` — popover
   *  placement against a section heading. `null` when no block owns the ref. */
  readonly rectForBlockRef: (blockRef: string) => DOMRect | null;
}

export function useReadCanvasSurface(): ReadCanvasSurface {
  const ref = useRef<ReadCanvasHandle | null>(null);
  const [anchorStates, setAnchorStates] = useState<Record<number, AnchorState>>(
    {},
  );
  const [anchorPoints, setAnchorPoints] = useState<SurfacePointMap | null>(null);

  const onAnchorStates = useCallback((states: Record<number, AnchorState>) => {
    setAnchorStates(states);
  }, []);
  const onAnchorPoints = useCallback((points: SurfacePointMap) => {
    setAnchorPoints(points);
  }, []);

  const focusAnchor = useCallback(
    (commentId: number, intent?: ScrollIntent): AnchorState | null =>
      ref.current?.focusAnchor(commentId, intent) ?? null,
    [],
  );
  const setPendingType = useCallback((type: string) => {
    ref.current?.setPendingType(type);
  }, []);
  const clearPending = useCallback(() => {
    ref.current?.clearPending();
  }, []);
  const setLinkedHover = useCallback((commentId: number | null) => {
    ref.current?.setLinkedHover(commentId);
  }, []);
  const requestComment = useCallback(
    (): boolean => ref.current?.requestComment() ?? false,
    [],
  );
  const rectForBlockRef = useCallback(
    (blockRef: string): DOMRect | null =>
      ref.current?.rectForBlockRef(blockRef) ?? null,
    [],
  );

  return useMemo(
    () => ({
      ref,
      anchorStates,
      anchorPoints,
      onAnchorStates,
      onAnchorPoints,
      focusAnchor,
      setPendingType,
      clearPending,
      setLinkedHover,
      requestComment,
      rectForBlockRef,
    }),
    [
      anchorStates,
      anchorPoints,
      onAnchorStates,
      onAnchorPoints,
      focusAnchor,
      setPendingType,
      clearPending,
      setLinkedHover,
      requestComment,
      rectForBlockRef,
    ],
  );
}
